/**
 * M16B-04 — Coordinator Attestation Lens (revised owner spec).
 *
 * Rendered inside the existing Coordinator Workspace as a tab.
 *
 * Strict scope:
 *  - Coordinator confirms COMPLETENESS / CONSISTENCY only.
 *  - Result is ONE of: complete | incomplete.
 *  - reason_text required when incomplete.
 *  - [Mark Complete] is disabled when:
 *       missing_fields.length > 0  OR  content_validation.blockers.length > 0
 *  - Profile change auto-marks attestation `stale` server-side.
 *  - No transition to treatment_ready here (M16B-05 future scope).
 */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import {
  ShieldCheck, AlertTriangle, RefreshCw, ChevronDown, ChevronRight,
  History, CheckCircle2, XCircle, Search,
} from 'lucide-react';
import api from '../../utils/api';
// M16B-07B — canonical Treatment Ready visibility (per-treatment-slug, read-only)
import TreatmentReadyPanel from '../../components/treatment-ready/TreatmentReadyPanel';

const STATUS_STYLE = {
  active:     { label: 'Active',     cls: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  stale:      { label: 'Stale',      cls: 'bg-amber-100  text-amber-800  border-amber-200' },
  superseded: { label: 'Superseded', cls: 'bg-slate-100  text-slate-700  border-slate-200' },
  revoked:    { label: 'Revoked',    cls: 'bg-rose-100   text-rose-800   border-rose-200' },
};
const RESULT_STYLE = {
  complete:   { label: 'Complete',   cls: 'bg-emerald-600 text-white' },
  incomplete: { label: 'Incomplete', cls: 'bg-amber-600   text-white' },
};

const GROUP_TONE = {
  A_identity:    'rose',
  B_emergency:   'rose',
  C_medical:     'rose',
  D_consents:    'rose',
  E_attachments: 'rose',
};

export default function CoordinatorAttestationLens() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [buckets, setBuckets] = useState({});
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  // Review panel state
  const [reviewPatientId, setReviewPatientId] = useState('');
  const [snap, setSnap] = useState(null);
  const [snapLoading, setSnapLoading] = useState(false);
  const [incompleteReason, setIncompleteReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/clinic/attestations');
      setItems(res.data.items || []);
      setBuckets(res.data.buckets || {});
    } catch (e) {
      toast.error('Failed to load attestations');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchList(); }, [fetchList]);

  const loadSnapshot = useCallback(async (pid) => {
    if (!pid) return;
    setSnapLoading(true);
    try {
      const res = await api.get(`/clinic/profile-completeness/${pid.trim()}`);
      setSnap(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Profile not found');
      setSnap(null);
    } finally { setSnapLoading(false); }
  }, []);

  const filtered = useMemo(
    () => (filter === 'all' ? items : items.filter(i => i.status === filter)),
    [items, filter],
  );

  const blockersExist = !!(snap?.content_validation?.blockers?.length);
  const missingExist  = !!(snap?.missing_fields?.length);
  const canMarkComplete = !!snap && !blockersExist && !missingExist;

  const submitResult = async (result) => {
    if (!snap) return;
    if (result === 'incomplete' && !incompleteReason.trim()) {
      toast.error('Reason text is required for Mark Incomplete');
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post('/clinic/attestations', {
        patient_id: snap.patient_id,
        result,
        reason_text: result === 'incomplete' ? incompleteReason.trim() : '',
      });
      toast.success(
        result === 'complete'
          ? `Attestation ${res.data.attestation.attestation_id} → complete (rev #${res.data.attestation.revision_no})`
          : `Attestation ${res.data.attestation.attestation_id} → incomplete (reason captured)`,
      );
      setIncompleteReason('');
      // Refresh both list + snapshot
      fetchList();
      loadSnapshot(snap.patient_id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Attestation failed');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="space-y-5" data-testid="attestation-lens">
      {/* HEADER + scope boundary banner */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="flex items-start gap-2">
          <ShieldCheck className="h-5 w-5 text-emerald-600 mt-0.5" />
          <div>
            <h2 className="text-base font-semibold text-slate-900">{t('provider.attestLens.pageHeading')}</h2>
            <p className="text-[12px] text-slate-600">
              Confirms <span className="font-semibold">completeness + consistency</span>. Medical correctness,
              treatment suitability, and risk remain doctor scope (M16B-05+).
            </p>
          </div>
        </div>
        <button
          onClick={fetchList}
          className="inline-flex items-center gap-1 px-2.5 py-1 text-xs text-slate-700 border border-slate-200 rounded-md hover:bg-slate-50"
        >
          <RefreshCw className="h-3 w-3" /> Refresh
        </button>
      </div>

      {/* REVIEW PANEL */}
      <div className="border border-slate-200 rounded-lg bg-white" data-testid="attestation-review-panel">
        <div className="px-4 py-3 border-b border-slate-100">
          <h3 className="text-sm font-semibold text-slate-900">Clinical Profile Review</h3>
          <p className="text-[11.5px] text-slate-500">{t('provider.attestLens.loadPatientHelp')}</p>
        </div>
        <div className="px-4 py-3 flex items-center gap-2">
          <Search className="h-4 w-4 text-slate-400" />
          <input
            data-testid="attestation-patient-id-input"
            type="text"
            value={reviewPatientId}
            onChange={(e) => setReviewPatientId(e.target.value)}
            placeholder={t('provider.attestLens.patientIdPh')}
            className="flex-1 text-sm font-mono px-2.5 py-1.5 border border-slate-300 rounded-md bg-white focus:ring-2 focus:ring-emerald-400 outline-none"
          />
          <Button
            size="sm"
            data-testid="attestation-load-snapshot"
            onClick={() => loadSnapshot(reviewPatientId)}
            disabled={snapLoading || !reviewPatientId.trim()}
          >
            {snapLoading ? t('provider.attestLens.loadingEllip') : t('provider.attestLens.loadProfile')}
          </Button>
        </div>

        {snap && (
          <div className="px-4 pb-4 space-y-3" data-testid="attestation-snapshot">
            {/* Completeness summary */}
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <Badge className={missingExist || blockersExist ? 'bg-amber-100 text-amber-800 border-amber-200' : 'bg-emerald-100 text-emerald-800 border-emerald-200'}>
                {missingExist || blockersExist ? 'Incomplete' : 'Complete'}
              </Badge>
              <span className="text-slate-700">{t('provider.attestLens.revisionPrefix')} <span className="font-mono">#{snap.revision_no}</span></span>
              <span className="text-slate-700">{t('provider.attestLens.tierPrefix')} <span className="font-mono">{snap.tier}</span></span>
              {snap.coordinator_attested && (
                <span className="text-emerald-700">
                  Coordinator attested · rev #{snap.coordinator_attestation_revision}
                </span>
              )}
            </div>

            {/* Missing groups */}
            <div data-testid="attestation-missing-groups">
              <div className="text-[11px] font-semibold text-slate-600 uppercase tracking-wide mb-1">{t('provider.attestLens.missingGroups')}</div>
              {snap.missing_groups?.length === 0 ? (
                <div className="text-xs text-emerald-700 inline-flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> All required groups present</div>
              ) : (
                <div className="flex flex-wrap gap-1">
                  {snap.missing_groups.map((g) => (
                    <span key={g} className="px-2 py-0.5 rounded bg-rose-50 border border-rose-200 text-rose-800 text-[11px]" data-testid={`missing-group-${g}`}>
                      {snap.group_labels?.[g] || g}
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Missing fields */}
            {snap.missing_fields?.length > 0 && (
              <div data-testid="attestation-missing-fields">
                <div className="text-[11px] font-semibold text-slate-600 uppercase tracking-wide mb-1">Missing fields ({snap.missing_fields.length})</div>
                <div className="flex flex-wrap gap-1">
                  {snap.missing_fields.map((f) => (
                    <span key={f} className="px-1.5 py-0.5 rounded bg-rose-50 border border-rose-200 text-rose-700 font-mono text-[10.5px]">
                      {f}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Content validation */}
            <div data-testid="attestation-content-validation">
              <div className="text-[11px] font-semibold text-slate-600 uppercase tracking-wide mb-1">{t('provider.attestLens.contentValidation')}</div>
              <div className={`px-2 py-1.5 rounded border text-xs ${blockersExist ? 'bg-amber-50 border-amber-200 text-amber-900' : 'bg-emerald-50 border-emerald-200 text-emerald-900'}`}>
                <div className="flex items-center gap-1 font-semibold">
                  {blockersExist ? <AlertTriangle className="h-3 w-3" /> : <CheckCircle2 className="h-3 w-3" />}
                  {blockersExist ? `${snap.content_validation.blockers.length} blocker(s)` : 'Content quality OK'}
                </div>
                {snap.content_validation.blockers.length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-1">
                    {snap.content_validation.blockers.map((b) => (
                      <span key={b} className="px-1.5 py-0.5 rounded bg-white border border-amber-300 text-amber-800 font-mono text-[10.5px]" data-testid={`blocker-${b}`}>
                        {b}
                      </span>
                    ))}
                  </div>
                )}
                {snap.content_validation.warnings.length > 0 && (
                  <div className="mt-1 text-[11px] text-amber-700">
                    Warnings: {snap.content_validation.warnings.join(', ')}
                  </div>
                )}
              </div>
            </div>

            {/* Latest attestation status */}
            {snap.latest_attestation && (
              <div className="text-[11px] text-slate-700 border border-slate-100 bg-slate-50 rounded px-2 py-1.5">
                <span className="font-semibold">{t('provider.attestLens.latestAttestation')} </span>
                {snap.latest_attestation.attestation_id} ·
                <span className="ml-1 font-mono">{snap.latest_attestation.result}</span> · rev #{snap.latest_attestation.revision_no}
                <span className="ml-1"> {t('provider.attestLens.statusSuffix')} <span className="font-mono">{snap.latest_attestation.status}</span></span>
              </div>
            )}

            {/* Scope warning */}
            <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 flex items-start gap-1">
              <AlertTriangle className="h-3 w-3 mt-0.5 flex-shrink-0" />
              You may NOT attest diagnosis, treatment suitability, medical correctness, or risk. Those are doctor-only.
            </div>

            {/* Actions */}
            <div className="space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  size="sm"
                  data-testid="attestation-mark-complete-btn"
                  onClick={() => submitResult('complete')}
                  disabled={submitting || !canMarkComplete}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <CheckCircle2 className="h-4 w-4 mr-1" />
                  Mark Complete
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  data-testid="attestation-mark-incomplete-btn"
                  onClick={() => submitResult('incomplete')}
                  disabled={submitting || !incompleteReason.trim()}
                  className="border-amber-300 text-amber-800 hover:bg-amber-50"
                >
                  <XCircle className="h-4 w-4 mr-1" />
                  Mark Incomplete
                </Button>
                {!canMarkComplete && (
                  <span className="text-[11px] text-rose-700" data-testid="attestation-disabled-reason">
                    Mark Complete disabled — {missingExist ? `${snap.missing_fields.length} missing field(s)` : ''}{missingExist && blockersExist ? ' · ' : ''}{blockersExist ? `${snap.content_validation.blockers.length} content blocker(s)` : ''}
                  </span>
                )}
              </div>
              <Textarea
                data-testid="attestation-reason-text"
                rows={2}
                value={incompleteReason}
                onChange={(e) => setIncompleteReason(e.target.value)}
                className="text-sm"
                placeholder="Reason text (required for Mark Incomplete) — e.g., 'emergency contact missing relationship; please re-upload passport, page 2 unreadable'"
              />
            </div>
          </div>
        )}
      </div>

      {/* LIST */}
      <div className="space-y-2">
        <div className="flex flex-wrap gap-2">
          {['all', 'active', 'stale', 'superseded', 'revoked'].map(b => (
            <button
              key={b}
              data-testid={`attestation-chip-${b}`}
              onClick={() => setFilter(b)}
              className={`px-3 py-1 text-xs rounded-full border ${
                filter === b
                  ? 'bg-slate-900 text-white border-slate-900'
                  : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
              }`}
            >
              {b.charAt(0).toUpperCase() + b.slice(1)}{' '}
              <span className="opacity-70">({buckets[b] ?? 0})</span>
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-sm text-slate-500 px-1 py-8 text-center">{t('provider.attestLens.loadingEllip')}</div>
        ) : filtered.length === 0 ? (
          <div className="text-sm text-slate-500 px-1 py-8 text-center border border-dashed border-slate-200 rounded-md">
            No attestations in <span className="font-medium">{filter}</span>.
          </div>
        ) : (
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600 text-xs">
                <tr>
                  <th className="text-left px-3 py-2 w-6"></th>
                  <th className="text-left px-3 py-2">{t('provider.attestLens.patient')}</th>
                  <th className="text-left px-3 py-2">{t('provider.attestLens.result')}</th>
                  <th className="text-left px-3 py-2">{t('provider.attestLens.revAttestedCurrent')}</th>
                  <th className="text-left px-3 py-2">{t('provider.attestLens.status')}</th>
                  <th className="text-left px-3 py-2">{t('provider.attestLens.by')}</th>
                  <th className="text-left px-3 py-2">{t('provider.attestLens.when')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((it) => {
                  const open = expandedId === it.attestation_id;
                  const sty = STATUS_STYLE[it.status] || STATUS_STYLE.active;
                  const rst = RESULT_STYLE[it.result] || { label: it.result, cls: 'bg-slate-200 text-slate-800' };
                  return (
                    <React.Fragment key={it.attestation_id}>
                      <tr
                        data-testid={`attestation-row-${it.attestation_id}`}
                        className={`hover:bg-slate-50 ${it.is_stale_now ? 'bg-amber-50/40' : ''}`}
                      >
                        <td className="px-2 py-2 align-top">
                          <button onClick={() => setExpandedId(open ? null : it.attestation_id)} className="text-slate-400 hover:text-slate-700">
                            {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                          </button>
                        </td>
                        <td className="px-3 py-2 align-top">
                          <div className="font-medium text-slate-800">{it.patient_name}</div>
                          <div className="text-[11px] font-mono text-slate-500">{String(it.patient_id).slice(0, 8)}</div>
                        </td>
                        <td className="px-3 py-2 align-top">
                          <Badge className={`text-[10.5px] font-medium ${rst.cls}`}>{rst.label}</Badge>
                        </td>
                        <td className="px-3 py-2 align-top font-mono text-xs">
                          #{it.revision_no} · #{it.current_revision_no}
                          {it.is_stale_now && <span className="ml-1 text-amber-700">{t('provider.attestLens.stale')}</span>}
                        </td>
                        <td className="px-3 py-2 align-top">
                          <Badge className={`text-[10.5px] ${sty.cls}`}>{sty.label}</Badge>
                        </td>
                        <td className="px-3 py-2 align-top text-xs">
                          <div className="text-slate-800">{it.coordinator_name}</div>
                          <div className="text-slate-500">{it.coordinator_role}</div>
                        </td>
                        <td className="px-3 py-2 align-top text-xs text-slate-700">
                          {(it.timestamp || it.created_at || '').slice(0, 16).replace('T', ' ')}
                        </td>
                      </tr>
                      {open && (
                        <tr className="bg-slate-50/60">
                          <td colSpan={7} className="px-6 py-3">
                            <div className="space-y-2">
                              {/* M16B-07B — coordinator read-only Treatment Ready visibility */}
                              <TreatmentReadyPanel
                                patientId={it.patient_id}
                                mode="coordinator"
                                title={t('provider.attestLens.treatmentReadinessRO')}
                                className="mb-2"
                              />
                              {it.reason_text && (
                                <div className="text-xs text-slate-700">
                                  <span className="font-semibold">{t('provider.attestLens.reasonPrefix')} </span>{it.reason_text}
                                </div>
                              )}
                              {(it.snapshot_missing?.length > 0) && (
                                <div className="text-[11.5px] text-slate-700">
                                  <span className="font-semibold">{t('provider.attestLens.snapshotMissing')} </span>
                                  <span className="font-mono">{it.snapshot_missing.join(', ')}</span>
                                </div>
                              )}
                              {(it.snapshot_blockers?.length > 0) && (
                                <div className="text-[11.5px] text-slate-700">
                                  <span className="font-semibold">{t('provider.attestLens.snapshotBlockers')} </span>
                                  <span className="font-mono">{it.snapshot_blockers.join(', ')}</span>
                                </div>
                              )}
                              <div className="text-xs text-slate-700 flex items-center gap-1">
                                <History className="h-3 w-3" />
                                <span className="font-semibold">{t('provider.attestLens.auditTrail')}</span>
                              </div>
                              <ol className="space-y-1 pl-1">
                                {(it.audit_trail || []).map((e, i) => (
                                  <li key={i} className="text-[11.5px] text-slate-700 font-mono">
                                    <span className="text-slate-400">{(e.at || '').slice(0, 19).replace('T', ' ')}</span>
                                    {' · '}
                                    <span className="font-semibold text-slate-900">{e.event}</span>
                                    {' '}{t('provider.attestLens.actorPrefix')}{' '}
                                    <span>{e.actor_email || e.actor}</span>
                                    {' ('}
                                    <span>{e.actor_role || t('provider.attestLens.system')}</span>
                                    {')'}
                                    {e.result && <span> {t('provider.attestLens.resultPrefix')} <span className="text-emerald-700">{e.result}</span></span>}
                                    {e.revision_no !== undefined && <span> {t('provider.attestLens.revPrefix')} #{e.revision_no}</span>}
                                    {e.from_rev !== undefined && <span> {t('provider.attestLens.revPrefix')} #{e.from_rev} → #{e.to_rev}</span>}
                                    {e.reason && <span className="text-amber-700"> {t('provider.attestLens.reasonAuditPrefix')} {e.reason}</span>}
                                    {e.reason_text && <span className="text-amber-700"> {t('provider.attestLens.notePrefix')} &quot;{e.reason_text}&quot;</span>}
                                  </li>
                                ))}
                              </ol>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
